import { supabase } from '../supabase';

// A paying coach's Plan & Code page (plan 014, step 4). What the page shows comes from the database in one call
// (migration 111, my_coach_plan); anything that touches Stripe goes through the server (api/coach-billing.js),
// which checks who is asking. Declining touches no money, so it goes straight to the database (104).

/** The signed-in coach's plan, or { result: 'no_plan' }. Throws if it could not be read. */
export async function loadMyCoachPlan() {
  const { data, error } = await supabase.rpc('my_coach_plan');
  if (error) throw error;
  if (!data?.result) throw new Error('Your plan could not be read. Please try again.');
  return data;
}

/** Turns down a client's request. Returns the database's answer: 'closed', 'no_such_request' or 'switched_off'. */
export async function declineRequest(place) {
  const { data, error } = await supabase.rpc('coach_decline_request', { p_place: place });
  if (error) throw error;
  return data?.result;
}

/** One of the server's coach billing actions: card_page, accept, close_attempt, leave, stay. */
export async function coachBilling(action, extra = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Please sign in again.');
  const res = await fetch('/api/coach-billing', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ action, ...extra }),
  });
  const body = await res.json().catch(() => ({}));
  // A 500 may follow a charge that went through, so the server's own words are shown, never "try again"
  if (!res.ok) throw new Error(body.error || 'Something went wrong. Please look again in a few minutes.');
  return body;
}

export const pounds = (pence) => (pence == null ? '' : `£${(pence / 100).toFixed(2)}`);

/** The bill for a month whose highest count was `clients`, at a price ({ base_pence, included, extra_pence }). */
export const billFor = (price, clients) =>
  price.base_pence + Math.max(0, Math.floor(clients) - price.included) * price.extra_pence;

/** "6 November, 14:00" (or "6 November" without the time) */
export const when = (iso, withTime = true) => {
  if (!iso) return '';
  const d = new Date(iso);
  const day = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
  return withTime ? `${day}, ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : day;
};

/**
 * What to tell the coach after Accept. The server's answer is passed through as it is (api/lib/coachBilling.js and
 * migrations 104 and 106); name is the client's short name, billBefore the next bill shown before Accept. Returns
 * { tone: 'good'|'info'|'bad', text, payUrl? }.
 */
export function acceptMessage(out, name, billBefore = null) {
  switch (out?.result) {
    case 'covered':
      // "stays" when this client is within what the month already counts (launch check, 4 November 2026)
      return { tone: 'good', text: out.next_bill_pence === billBefore
        ? `${name} is in. Your next bill stays ${pounds(out.next_bill_pence)}.`
        : `${name} is in. Your next bill is now ${pounds(out.next_bill_pence)}.` };
    case 'joined_as_paying_member':
      return { tone: 'good', text: `${name} has joined you. They pay for themselves, so they cost you nothing.` };
    case 'let_in':
      return { tone: 'good', text: `Your payment went through and ${name} is in.` };
    case 'needs_coach':
      return out.pay_url
        ? { tone: 'info', text: `Your bank wants you to approve this payment. ${name} joins once it has gone through.`, payUrl: out.pay_url }
        : { tone: 'info', text: `Your payment is waiting for your bank. ${name} joins once it has gone through.` };
    case 'closed':
      return { tone: 'bad', text: 'The payment did not go through, and nothing was charged. If your client is still waiting, you can try again, or add another card first.' };
    case 'paid_with_no_client':
      return { tone: 'info', text: `Your payment went through, but ${name} was no longer waiting. You have paid for this month and can accept someone else. Cipher has been told.` };
    case 'checking':
    case 'still_checking':
    case 'payment_under_way':
      return { tone: 'info', text: 'We are checking your payment with the bank. Look again in a few minutes before trying anything else.' };
    case 'needs_owner':
    case 'history_unknown':
      return { tone: 'info', text: `We need to check this by hand. Cipher has been told and will be in touch. ${name}'s request stays open until it runs out.` };
    case 'already_finished':
    case 'already_done':
    case 'no_such_task':
      return { tone: 'info', text: 'That payment has already been settled. The page now shows where things stand.' };
    case 'old_attempt_paid':
      return { tone: 'info', text: 'An earlier payment attempt went through. Cipher has been told and will sort it out with you.' };
    case 'no_plan':
      return { tone: 'bad', text: 'You are no longer on a paying plan. Please contact Cipher.' };
    case 'request_expired':
    case 'no_such_request':
      return { tone: 'info', text: `${name}'s request has ended.` };
    case 'client_moved_on':
    case 'client_not_active':
    case 'already_covered':
      return { tone: 'info', text: `${name} is no longer waiting for you.` };
    case 'coach_is_full':
      return { tone: 'bad', text: `You have reached your limit, so ${name} could not join and has been told. Please contact Cipher.` };
    case 'plan_not_open':
      return { tone: 'bad', text: 'You cannot accept new clients right now. The page shows why.' };
    case 'busy':
    case 'try_again':
      return { tone: 'info', text: 'Another change to your plan is going through. Try again in a moment.' };
    case 'switched_off':
      return { tone: 'info', text: 'Coach payments are not switched on yet.' };
    default:
      return { tone: 'info', text: 'The page now shows where things stand.' };
  }
}
